import React, { useState } from "react"

import { RouteComponentProps } from "react-router-dom"
import { gql, useQuery } from "urql"

import { Tooltip } from "@chakra-ui/core"
import {
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline"

import { Color } from "~tailwindui/types/enums"

import NotFound from "~Tutor/Pages/NotFound"
import { path } from "~Tutor/TutorRoutes"
import { H1, H2, StyledLink, WithLoadingIndicator } from "~tailwindui/Basics"
import Paginator from "~tailwindui/Table/Paginator"

import { MatchParams } from "src/types/MatchParams"

const TailoredAssignments: React.FC<RouteComponentProps<MatchParams>> = ({
  match,
}) => {
  const studentId = match.params.id
  const [page, setPage] = useState(1)

  const [linkCopiedForId, setLinkCopiedForId] = useState<string | null>()

  const [result] = useQuery({
    query: loadAssignmentsQuery,
    variables: { studentId, page },
  })

  if (result.data && !result.data.tailoredAssignmentSummaries)
    return <NotFound />

  const addToClipboard = (link: string, id: string) => {
    navigator.clipboard.writeText(link)
    setLinkCopiedForId(id)
    setTimeout(() => setLinkCopiedForId(null), 2000)
  }

  return (
    <>
      <H1>Tailored Assignments</H1>
      <WithLoadingIndicator
        result={result}
        error="An error occurred while loading assignments"
      >
        {({ data: { student, tailoredAssignmentSummaries } }) =>
          tailoredAssignmentSummaries.data.length === 0 ? (
            <span className="italic">
              There are no tailored assignments for this student.
            </span>
          ) : (
            <>
              <ul className="divide-y">
                {tailoredAssignmentSummaries.data.map(summary => (
                  <li
                    className="space-y-2 pb-6 text-lg"
                    key={summary.tailoredAssignment.id}
                  >
                    <H2 className="mb-0">{summary.tailoredAssignment.name}</H2>

                    <Tooltip
                      label="This link will work for all students who have access to this assignment."
                      className="rounded-md bg-gray-600 py-1 px-2 text-white"
                      aria-label="Copy Assignment Link for Students"
                    >
                      <span
                        className="inline-flex cursor-pointer items-center hover:underline"
                        onClick={() =>
                          addToClipboard(
                            summary.studentLink,
                            summary.tailoredAssignment.id
                          )
                        }
                      >
                        Click to copy Student Link
                        {linkCopiedForId === summary.tailoredAssignment.id && (
                          <ClipboardDocumentCheckIcon className="ml-1 h-6 w-6 text-green-400" />
                        )}
                        {linkCopiedForId !== summary.tailoredAssignment.id && (
                          <ClipboardDocumentIcon className="ml-1 h-6 w-6" />
                        )}
                      </span>
                    </Tooltip>

                    <div>
                      <StyledLink
                        to={path("tailoredAssignmentPreview", {
                          studentId,
                          id: summary.tailoredAssignment.id,
                        })}
                        button
                      >
                        Preview Assignment
                      </StyledLink>
                    </div>

                    <div>
                      {student.firstName}'s Score:{" "}
                      {summary.aasmState === "initial" && (
                        <span>Not Started</span>
                      )}
                      {summary.aasmState === "in_progress" && (
                        <span>In Progress</span>
                      )}
                      {summary.aasmState === "completed" && (
                        <span>{(summary.score * 100).toFixed(2)}%</span>
                      )}
                    </div>

                    {summary.aasmState !== "initial" && (
                      <div>
                        <StyledLink
                          to={path("studentTailoredAssignmentResults", {
                            studentId,
                            id: summary.tailoredAssignment.id,
                          })}
                          button
                          color={Color.Green}
                        >
                          View Section Results
                        </StyledLink>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              <Paginator
                currentPage={page}
                totalPages={tailoredAssignmentSummaries.totalPages}
                changePage={page => setPage(page)}
              />
            </>
          )
        }
      </WithLoadingIndicator>
    </>
  )
}

const loadAssignmentsQuery = gql`
  query TailoredAssignments($studentId: ID!, $page: Int!) {
    student(id: $studentId) {
      id
      firstName
    }
    tailoredAssignmentSummaries(studentId: $studentId, page: $page) {
      currentPage
      totalPages
      data {
        score
        aasmState
        studentLink
        tailoredAssignment {
          id
          name
        }
      }
    }
  }
`

export default TailoredAssignments
